import { Injectable, inject, signal } from '@angular/core';
import { ServiceBase } from '../../shared/models/bases';
import { AuthenticatedUser } from '../../shared/models/auth';
import { AuthenticatedUserStore } from '../../stores/auth';
import { API_ENDPOINTS } from '../../shared/common/apiConstants';
import { jwtDecode } from 'jwt-decode';
import { JwtPayload } from '../../shared/interfaces';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MembershipService } from '../membership/membership.service';

const STORAGE_KEY = 'auth_data';

@Injectable({ providedIn: 'root' })
export class AuthService extends ServiceBase<AuthenticatedUser> {
  private _authData: AuthenticatedUser | null = null;
  private permissions$ = new BehaviorSubject<Set<string>>(new Set());
  private httpClient = inject(HttpClient);
  private membershipService = inject(MembershipService);
  private userInfoSignal = signal<JwtPayload | null>(null);

  constructor(authenticatedUserStore: AuthenticatedUserStore) {
    super(API_ENDPOINTS.auth.login, authenticatedUserStore);

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      parsed.expiresAt = new Date(parsed.expiresAt);
      if (new Date() < parsed.expiresAt) {
        this._authData = parsed;
        this.updateUserInfoSignal();
      } else {
        this.logout();
      }
    }
  }

  login(response: AuthenticatedUser): void {
    this._authData = response;
    const token = JSON.stringify(response);
    localStorage.setItem(STORAGE_KEY, token);
    this.hydrateFromToken(token);
    this.updateUserInfoSignal();

    // Membership status will be checked by auth guard
  }

  logout(): void {
    this._authData = null;
    this.permissions$.next(new Set());
    this.membershipService.resetMembershipState();
    this.clearClientStorage();
    this.userInfoSignal.set(null);
  }

  /**
   * Check membership status on app startup
   */
  checkMembershipOnStartup(): Observable<any> {
    if (this.isLoggedIn()) {
      return this.membershipService.getMembershipStatus();
    }
    return of(null);
  }

  getToken(): string | null {
    return this._authData?.token ?? null;
  }

  getBusinessIsActive(): boolean | null {
    return this._authData?.business_is_active ?? false;
  }

  getUserInfo() {
    const info = this.userInfoSignal();
    if (info) return info;
    return this.updateUserInfoSignal();
  }

  getBusinessName() {
    // If impersonating, return the impersonated business name
    if (this.isImpersonating()) {
      const userInfo = this.getUserInfo();
      const businessName = userInfo?.business_name;
      if (Array.isArray(businessName)) {
        return businessName[0] || 'Negocio';
      }
      return businessName || 'Negocio';
    }

    // If sysadmin and not impersonating, return "Sistema"
    if (this.isSystemAdmin()) {
      return 'Sistema';
    }

    // Otherwise, return the business name from token or user info
    const defaultBusinessName = this.getUserInfo()?.business_name;
    const token = localStorage.getItem(STORAGE_KEY);
    if (token) {
      const businessName = JSON.parse(token)?.business_name;
      if (Array.isArray(businessName)) {
        return businessName[0] || defaultBusinessName;
      }
      if (businessName) return businessName;
      return defaultBusinessName;
    }
    if (Array.isArray(defaultBusinessName)) {
      return defaultBusinessName[0];
    }
    return defaultBusinessName;
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const { exp } = jwtDecode<any>(token);
    return Date.now() < exp * 1000;
  }

  getUser() {
    return this._authData?.user ?? null;
  }

  private hydrateFromToken(token: string) {
    const payload = jwtDecode<JwtPayload>(token);
    const perms = new Set<string>(payload.permission ?? []);
    this.permissions$.next(perms);
  }

  loadFromStorage() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        parsed.expiresAt = new Date(parsed.expiresAt);
        if (new Date() < parsed.expiresAt) {
          this._authData = parsed;
          this.hydrateFromToken(stored);
          this.updateUserInfoSignal();
        } else {
          this.logout();
        }
      } catch (error) {
        console.error('Error loading auth data from storage:', error);
        this.logout();
      }
    }
  }

  private writeUserToStorage(user: AuthenticatedUser | null): void {
    if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY);
  }

  get permissionsChanges() {
    return this.permissions$.asObservable();
  }

  has(permission: string): boolean {
    return this.permissions$.value.has(permission);
  }

  hasAny(perms: string[]): boolean {
    // Check if user is sysadmin - sysadmin has access to everything
    if (this.isSystemAdmin()) {
      return true;
    }
    // Special check for 'sysadmin' permission/role
    if (perms.includes('sysadmin')) {
      return this.isSystemAdmin();
    }
    const set = this.permissions$.value;
    return perms.some((p) => set.has(p));
  }

  hasAll(perms: string[]): boolean {
    const set = this.permissions$.value;
    return perms.every((p) => set.has(p));
  }

  markBusinessActive(newBusinessName: string): void {
    const current = this._authData;

    if (!current) return;
    const updated: AuthenticatedUser = {
      ...current,
      business_is_active: true,
      business_name: newBusinessName,
    };
    this._authData = updated;
    this.writeUserToStorage(updated);
    this.updateUserInfoSignal({
      business_is_active: true,
      business_name: newBusinessName,
    });
  }

  updateBusinessName(newBusinessName: string): void {
    const current = this._authData;
    if (!current) return;
    const updated: AuthenticatedUser = {
      ...current,
      business_name: newBusinessName,
    };
    this._authData = updated;
    this.writeUserToStorage(updated);
    this.updateUserInfoSignal({
      business_name: newBusinessName,
    });
  }

  /**
   * Authenticate with Google using ID token
   */
  authenticateWithGoogle(idToken: string): Observable<AuthenticatedUser> {
    const payload = { idToken };
    return this.httpClient.post<AuthenticatedUser>(
      this.baseUrl + API_ENDPOINTS.auth.google,
      payload,
    );
  }

  /**
   * Complete Google signup process
   */
  completeGoogleSignup(data: any): Observable<AuthenticatedUser> {
    return this.httpClient.post<AuthenticatedUser>(
      this.baseUrl + API_ENDPOINTS.auth.completeGoogleSignup,
      data,
    );
  }

  requestPasswordReset(
    email: string,
  ): Observable<{ message: string; isGoogleLinked?: boolean }> {
    return this.httpClient.post<{ message: string; isGoogleLinked?: boolean }>(
      this.baseUrl + API_ENDPOINTS.auth.forgotPassword,
      { email },
    );
  }

  resetPassword(
    token: string,
    password: string,
  ): Observable<{ message: string }> {
    return this.httpClient.post<{ message: string }>(
      this.baseUrl + API_ENDPOINTS.auth.resetPassword,
      { token, password },
    );
  }

  get userInfoChanges() {
    return this.userInfoSignal.asReadonly();
  }

  private updateUserInfoSignal(overrides?: Partial<JwtPayload>) {
    const token = this.getToken();
    if (!token) {
      this.userInfoSignal.set(null);
      return null;
    }
    const payload = jwtDecode<JwtPayload>(token);
    const merged = overrides ? { ...payload, ...overrides } : payload;
    this.userInfoSignal.set(merged);
    return merged;
  }

  private clearClientStorage() {
    try {
      localStorage.clear();
    } catch (err) {
      console.warn('Unable to clear localStorage on logout:', err);
    }
    try {
      sessionStorage.clear();
    } catch (err) {
      console.warn('Unable to clear sessionStorage on logout:', err);
    }
  }

  /**
   * Check if the current user is a system admin (sysadmin role)
   * Checks for both "sysadmin" (code) and "Administrador del Sistema" (name)
   */
  isSystemAdmin(): boolean {
    const roles = this.getRolesFromToken();
    return (
      roles.includes('sysadmin') || roles.includes('Administrador del Sistema')
    );
  }

  /**
   * Check if the current user has the owner role
   * Checks for both "owner" (code) and "Propietario" (name)
   */
  isOwner(): boolean {
    const roles = this.getRolesFromToken();
    return roles.includes('Administrador') || roles.includes('Propietario');
  }

  getDisplayRoleName(): string | null {
    const roles = this.getRolesFromToken();
    if (roles.length === 0) {
      const role = this.getUserInfo()?.role;
      return Array.isArray(role) ? role[0] ?? null : role ?? null;
    }

    const priority = [
      'Propietario',
      'Administrador',
      'Gerente',
      'Recepcionista',
      'Cajero/Finanzas',
      'Marketing',
      'Profesional',
      'Solo lectura',
      'Cliente',
    ];

    for (const role of priority) {
      if (roles.includes(role)) return role;
    }

    return roles[0] ?? null;
  }

  /**
   * Check if the current user is impersonating a business
   */
  isImpersonating(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const payload = jwtDecode<any>(token);
      return payload.impersonating === true || payload.impersonating === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Get the original user ID when impersonating
   */
  getOriginalUserId(): string | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = jwtDecode<any>(token);
      return payload.original_user_id || null;
    } catch {
      return null;
    }
  }

  private getRolesFromToken(): string[] {
    const token = this.getToken();
    if (!token) return [];
    try {
      const payload = jwtDecode<any>(token);
      // JWT can have multiple role claims - check all possible locations
      // .NET serializes ClaimTypes.Role as "http://schemas.microsoft.com/ws/2008/06/identity/claims/role" or "role"
      const roleKey =
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
      const roles: string | string[] = payload[roleKey] || payload.role || [];
      if (Array.isArray(roles)) return roles;
      if (typeof roles === 'string') return [roles];
      return [];
    } catch {
      return [];
    }
  }
}
